import React, { useState, useEffect } from 'react'
import { T, Spinner, Empty } from '../ui'
import { getNFLCascadeTeams, getNFLCascade } from '../api'

// Injury cascade — flip a starter out and the model reprojects his whole position
// group. Vacated rush/target/red-zone volume flows to the players behind him;
// their fantasy points and anytime-TD% jump. This is the "who inherits the
// touches?" simulator, wired to the real depth-chart + red-zone-role data.

const posMetrics = (pos, b) => {
  const mb = b.mk_before || {}, ma = b.mk_after || {}
  const pct = v => `${Math.round((v || 0) * 100)}%`
  if (pos === 'QB') return [
    ['Pass Yds', mb.pass_yards, ma.pass_yards, ''],
    ['Pass TD', mb.pass_tds, ma.pass_tds, ''],
    ['Fantasy', b.pts_before, b.pts_after, ''],
  ]
  if (pos === 'RB') return [
    ['Rush Yds', mb.rush_yards, ma.rush_yards, ''],
    ['Rec', mb.receptions, ma.receptions, ''],
    ['TD', pct(b.td_before), pct(b.td_after), '_str'],
    ['Fantasy', b.pts_before, b.pts_after, ''],
  ]
  return [
    ['Rec', mb.receptions, ma.receptions, ''],
    ['Rec Yds', mb.rec_yards, ma.rec_yards, ''],
    ['TD', pct(b.td_before), pct(b.td_after), '_str'],
    ['Fantasy', b.pts_before, b.pts_after, ''],
  ]
}

function Metric({ label, before, after, isStr }) {
  const up = isStr ? parseFloat(after) > parseFloat(before) : after > before
  return (
    <div style={{ textAlign: 'center', minWidth: 0 }}>
      <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
      <div style={{ fontFamily: T.mono, fontWeight: 600, fontSize: 15, marginTop: 3, color: up ? T.green : T.text }}>{after}</div>
      <div style={{ fontFamily: T.mono, fontSize: 10, color: T.faint, marginTop: 1, height: 11 }}>
        {up ? `↑ ${before}` : ''}
      </div>
    </div>
  )
}

function Beneficiary({ b, hot }) {
  const mets = posMetrics(b.pos, b)
  return (
    <div style={{ background: T.card, border: `1px solid ${hot ? T.green : T.border}`, borderRadius: 14,
      padding: '12px 13px', marginBottom: 9 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 14.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {b.name} <span style={{ color: T.muted, fontWeight: 400, fontSize: 11 }}>{b.pos}</span></div>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.04em', padding: '3px 7px', borderRadius: 6, flexShrink: 0,
          color: hot ? '#04140C' : T.faint, background: hot ? T.green : 'transparent', border: `1px solid ${hot ? T.green : T.border}` }}>{b.role}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${mets.length},1fr)`, gap: 8 }}>
        {mets.map(([l, bef, aft, s]) => <Metric key={l} label={l} before={bef} after={aft} isStr={s === '_str'} />)}
      </div>
      <div style={{ marginTop: 9, fontSize: 11, color: T.muted, textAlign: 'right' }}>
        <b style={{ color: T.green, fontFamily: T.mono }}>+{b.delta}</b> proj pts
      </div>
    </div>
  )
}

function Vacated({ out, vac }) {
  const cells = out.pos === 'QB'
    ? [[vac.pass_yards, 'pass yds'], [vac.td_pg, 'TD/g']]
    : out.pos === 'RB'
      ? [[vac.rush_yards, 'rush yds'], [vac.receptions, 'rec'], [vac.td_pg, 'TD/g'], [vac.gl_touches, 'goal-line']]
      : [[vac.receptions, 'rec'], [vac.rec_yards, 'rec yds'], [vac.td_pg, 'TD/g'], [`${Math.round((vac.rz_share || 0) * 100)}%`, 'RZ share']]
  return (
    <div style={{ background: `linear-gradient(180deg, ${T.redDim}, ${T.card})`, border: `1px solid ${T.red}55`,
      borderRadius: 15, padding: 14, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 800, fontSize: 16 }}>{out.name}
          <span style={{ color: T.muted, fontWeight: 500, fontSize: 12, marginLeft: 6 }}>{out.pos} · {out.team}</span></div>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', color: T.red,
          border: `1px solid ${T.red}66`, borderRadius: 6, padding: '3px 8px' }}>✕ OUT</span>
      </div>
      <div style={{ fontSize: 10, color: T.red, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 700, margin: '12px 0 8px' }}>Vacates</div>
      <div style={{ display: 'flex', gap: 8 }}>
        {cells.map(([n, l], i) => (
          <div key={i} style={{ flex: 1, background: T.card2, border: `1px solid ${T.border}`, borderRadius: 9, padding: '8px 4px', textAlign: 'center' }}>
            <div style={{ fontFamily: T.mono, fontWeight: 600, fontSize: 15, color: T.red }}>{n}</div>
            <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function CascadePanel({ season, week, scoring }) {
  const [meta, setMeta] = useState(null)
  const [team, setTeam] = useState(null)
  const [outId, setOutId] = useState(null)
  const [res, setRes] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let alive = true
    getNFLCascadeTeams(season, week, scoring).then(d => {
      if (!alive) return
      setMeta(d)
      const live = (d.live || [])[0]
      if (live) { setTeam(live.team); setOutId(live.gsis_id) }
      else if (d.teams && d.teams[0]) setTeam(d.teams[0].team)
    })
    return () => { alive = false }
  }, [season, week, scoring])

  useEffect(() => {
    if (!outId) { setRes(null); return }
    let alive = true; setLoading(true)
    getNFLCascade(outId, season, week, scoring).then(d => { if (alive) { setRes(d); setLoading(false) } })
    return () => { alive = false }
  }, [outId, season, week, scoring])

  if (!meta) return <Spinner />
  if (!meta.teams || !meta.teams.length)
    return <Empty message="Not available yet — this week's projections load midweek. Check back near kickoff." />

  const teamObj = meta.teams.find(t => t.team === team)
  const benIds = new Set((res?.beneficiaries || []).map(b => b.gsis_id))

  return (
    <div>
      {meta.live && meta.live.length > 0 && (
        <div style={{ background: T.redDim, border: `1px solid ${T.red}55`, borderRadius: 12, padding: '10px 12px', marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', color: T.red, textTransform: 'uppercase', marginBottom: 7 }}>● Ruled out this week</div>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {meta.live.map(p => (
              <button key={p.gsis_id} onClick={() => { setTeam(p.team); setOutId(p.gsis_id) }}
                style={{ fontSize: 11.5, fontWeight: 700, cursor: 'pointer', padding: '5px 10px', borderRadius: 8,
                  color: outId === p.gsis_id ? '#0A0605' : T.text, background: outId === p.gsis_id ? T.red : T.card,
                  border: `1px solid ${T.red}66` }}>{p.name} · {p.pos}</button>
            ))}
          </div>
        </div>
      )}

      <div style={{ fontSize: 10.5, color: T.muted, marginBottom: 7 }}>Pick a team, then flip a starter out</div>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 13, scrollbarWidth: 'none' }}>
        {meta.teams.map(t => (
          <button key={t.team} onClick={() => { setTeam(t.team); setOutId(null) }}
            style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 20, cursor: 'pointer', fontWeight: 800, fontSize: 12,
              border: `1px solid ${team === t.team ? T.green : T.border}`, background: team === t.team ? T.green : T.card,
              color: team === t.team ? '#04140D' : T.muted }}>{t.team}</button>
        ))}
      </div>

      {teamObj && (
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 16 }}>
          {teamObj.players.map(p => {
            const on = outId === p.gsis_id
            return (
              <button key={p.gsis_id} onClick={() => setOutId(on ? null : p.gsis_id)}
                style={{ cursor: 'pointer', padding: '7px 11px', borderRadius: 10, textAlign: 'left',
                  border: `1px solid ${on ? T.red : p.injured ? `${T.red}66` : T.border}`,
                  background: on ? `linear-gradient(180deg, ${T.redDim}, ${T.card})` : T.card, color: T.text }}>
                <div style={{ fontSize: 12.5, fontWeight: 700 }}>{on ? '✕ ' : ''}{p.name}
                  {p.injured && !on && <span style={{ color: T.red, marginLeft: 5, fontSize: 10 }}>OUT</span>}</div>
                <div style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>{p.pos} · {p.pts} proj</div>
              </button>
            )
          })}
        </div>
      )}

      {loading ? <Spinner />
        : res && res.out ? (
          <div>
            <Vacated out={res.out} vac={res.vacated} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '4px 2px 11px' }}>
              <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', color: T.muted, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>The cascade</span>
              <span style={{ flex: 1, height: 1, background: T.border2 }} />
            </div>
            {res.beneficiaries.length
              ? res.beneficiaries.map((b, i) => <Beneficiary key={b.gsis_id} b={b} hot={i < 2 && benIds.has(b.gsis_id)} />)
              : <Empty message="No clear beneficiary in the depth chart for this player." />}
            {res.angle && (
              <div style={{ background: T.greenDim, border: `1px solid ${T.green}55`, borderRadius: 12, padding: '12px 13px', marginTop: 4 }}>
                <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.05em', color: T.green, textTransform: 'uppercase', marginBottom: 6 }}>The angle</div>
                <div style={{ fontSize: 12.5, color: T.text, lineHeight: 1.5 }}>{res.angle}</div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '30px 16px', color: T.muted }}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>⚡</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Flip a starter out to see who inherits the touches.</div>
          </div>
        )}
    </div>
  )
}
